import { useReactiveVar } from '@apollo/client';
import { useLocation, useNavigate, useParams } from '@hulu/react-router-dom';
import debounce from 'lodash/debounce';
import React, { useContext, useMemo, useState } from 'react';
import { HeaderGroup, useFilters, useRowSelect, useSortBy, useTable } from 'react-table';

import { DynamicFiltersFields } from '../../../../apis/graphql/reports/standardDeliveryDHC/types';
import useCreateStandardDeliveryDHCReport from '../../../../apis/graphql/reports/standardDeliveryDHC/useCreateStandardDeliveryDHCReport';
import useStandardDeliveryDHCReportMetrics from '../../../../apis/graphql/reports/standardDeliveryDHC/useStandardDeliveryDHCReportMetrics';
import useUpdateStandardDeliveryDHCReport from '../../../../apis/graphql/reports/standardDeliveryDHC/useUpdateStandardDeliveryDHCReport';
import { StandardDeliveryDeviceHigherCategoryExportReport } from '../../../../apis/graphql/reports/types';
import { getMetricOptions } from '../../../../apis/graphql/reports/utils';
import { getCampaignIds } from '../../../../apis/graphql/reports/utils/utils';
import {
  ExportFormat,
  ReportInput,
  StandardDeliveryDeviceHigherCategoryReport,
  StandardDeliveryDeviceHigherCategoryReportFiltersInput,
  UpdateReportInput,
} from '../../../../apis/graphql/types';
import { Loader, LoaderSize } from '../../../../components';
import admanToast from '../../../../components/Alert/util/admanToast';
import { CAMPAIGNS_TOOLTIP_CLASS } from '../../../../components/baymax/CampaignsTooltip/constants';
import { EmptyState, Notification, Pagination, SideBar } from '../../../../components/BlueTheme';
import { NotificationType } from '../../../../components/BlueTheme/Notification/types';
import { Header } from '../../../../components/BlueTheme/Table';
import { StyledTable } from '../../../../components/BlueTheme/Table/styled';
import { selectedOrganizationIdVar } from '../../../../constants';
import { localStorageGhostModeName } from '../../../../constants/ghostMode';
import { GlobalPermissionsContext } from '../../../../context';
import { GhostModeContext } from '../../../../context/GhostMode/GhostMode';
import { useExportReport } from '../../../../hooks/baymax';
import useDoesUserIdMatchEntityId from '../../../../hooks/useDoesUserIdMatchEntityId';
import useSetUserFromCurrentSession from '../../../../hooks/useSetUserFromCurrentSession';
import { StorageEntity } from '../../../../utils/storages/storageEntity';
import { prepareSortOptions } from '../../../../utils/table';
import { DynamicFiltersFields as CommonDynamicFiltersFields } from '../../types/reports';
import { getBadgeFilters } from '../../utils/filters';
import Charts from '../Charts';
import { LINE_ITEM_END_DATE_COLUMN_ID, LINE_ITEM_NAME_COLUMN_ID, LINE_ITEM_START_DATE_COLUMN_ID } from '../constants';
import { ReportForm, ReportFormValues } from '../Shared';
import ReportTableHeader from '../Shared/ReportTableHeader/ReportTableHeader';
import { getInitialValuesForReportForm } from '../Shared/utils';
import { BodyWrapper, ReportContainer, TableContainer } from '../styled';
import { getReportInput, getUpdateReportInput } from '../utils';
import { getStandardDeliveryDHVReportColumns } from './columns';
import { ReportFilterStorage, StandardDeliveryDHCReportTableData } from './types';
import { flattenMetrics, getStandardDeliveryDHCReportFilters } from './utils';

type Props = {
  storage?: StorageEntity<ReportFilterStorage>;
  report?: StandardDeliveryDeviceHigherCategoryReport;
  templateName: string;
};

const StandardDeliveryDHC = ({ storage, report, templateName }: Props): JSX.Element => {
  const { state, pathname } = useLocation();
  const { adEntityId, reportTemplateId } = useParams();
  const organizationId = useReactiveVar(selectedOrganizationIdVar) || '';
  const navigate = useNavigate();
  const [sidebarOpened, setSidebarOpened] = useState((state && (state as { onEdit: boolean }).onEdit) || false);
  const { handleCreateStandardDeliveryDHCReport, loading: savingReport } = useCreateStandardDeliveryDHCReport();
  const { handleUpdateStandardDeliveryDHCReport, loading: updatingReport } = useUpdateStandardDeliveryDHCReport();
  const { download, loading: exportReportLoading } = useExportReport(templateName);
  const { userInfo } = useSetUserFromCurrentSession();
  const { isGhostMode } = useContext(GhostModeContext);
  const ghostModeData = localStorage.getItem(localStorageGhostModeName);
  const ghostData = ghostModeData ? JSON.parse(ghostModeData) : undefined;
  const { CREATE_REPORT, UPDATE_REPORT, BUILD_REPORT } = useContext(GlobalPermissionsContext).permissions;

  const doesUserOwnReport = useDoesUserIdMatchEntityId(report?.ownerId);

  const {
    data: reports,
    loading,
    error,
    handleChangePage,
    handleSort,
    handleSearch,
    paginationOptions,
    sortBy,
    total,
    filters,
    searchValue,
    dynamicFilters,
    handleFilter,
    resetAllFilters,
  } = useStandardDeliveryDHCReportMetrics({ organizationId, advertiserId: adEntityId, reportTemplateId, storage });

  const preparedData: StandardDeliveryDHCReportTableData[] = useMemo(() => flattenMetrics(reports), [reports]);

  const reportColumns = useMemo(() => {
    return getStandardDeliveryDHVReportColumns(handleFilter);
  }, [handleFilter]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setFilter } = useTable(
    {
      columns: reportColumns,
      data: preparedData,
      manualSortBy: true,
      manualFilters: true,
      initialState: {
        sortBy,
        filters: [
          {
            id: LINE_ITEM_NAME_COLUMN_ID,
            value: searchValue,
          },
          {
            id: 'cpm',
            value: dynamicFilters?.cpm,
          },
          {
            id: 'revenue',
            value: dynamicFilters?.revenue,
          },
          {
            id: 'adProduct',
            value: dynamicFilters?.adProduct,
          },
          {
            id: 'publisher',
            value: dynamicFilters?.publisher,
          },
          {
            id: 'deliveryGoal',
            value: dynamicFilters?.deliveryGoal,
          },
          {
            id: 'lifetimeCompletions',
            value: dynamicFilters?.lifetimeCompletions,
          },
          {
            id: 'startDate',
            value: dynamicFilters?.startDate,
          },
          {
            id: 'endDate',
            value: dynamicFilters?.endDate,
          },
        ],
      },
    },
    useFilters,
    useSortBy,
    useRowSelect
  );

  const handleSearchDebounced = debounce((searchTerm: string) => {
    setFilter('lineItemName', searchTerm);
    setFilter('campaignName', searchTerm);
    return handleSearch(searchTerm);
  }, 300);

  const handleSorting = ({ id, isSorted, isSortedDesc }: HeaderGroup<StandardDeliveryDHCReportTableData>): void => {
    handleSort(id, isSorted, isSortedDesc);
  };

  const handleScheduleClicked = (): void => {
    setSidebarOpened(true);
  };

  const handleCloseSidebar = (): void => {
    setSidebarOpened(false);
  };

  const handleGenerateReport = async (exportFormat: ExportFormat): Promise<void> => {
    const { dateRange, endDate, startDate } = getMetricOptions(reportTemplateId, filters);
    const exportFilters: StandardDeliveryDeviceHigherCategoryExportReport = {
      __typename: 'StandardDeliveryDeviceHigherCategoryReportFilters',
      ...getStandardDeliveryDHCReportFilters(organizationId, adEntityId, dynamicFilters, filters.campaigns),
    };

    await download(
      {
        dateRange,
        exportFormat,
        templateId: reportTemplateId,
        endDate: endDate || null,
        startDate: startDate || null,
        filters: exportFilters,
      },
      {
        ...prepareSortOptions(sortBy),
        searchTerm: searchValue,
      }
    );
  };

  const onSaveReport = async (formValues: ReportFormValues): Promise<void> => {
    const reportInput: ReportInput = getReportInput({
      templateId: reportTemplateId,
      reportFormValues: formValues,
      userId: isGhostMode && ghostData ? ghostData.userId : userInfo?.ams_user_id || '',
      filters,
    });

    const standardDeliveryReportFilters: StandardDeliveryDeviceHigherCategoryReportFiltersInput =
      getStandardDeliveryDHCReportFilters(organizationId, adEntityId, dynamicFilters, filters.campaigns);

    try {
      if (report) {
        const updateReportInput: UpdateReportInput = getUpdateReportInput({
          templateId: reportTemplateId,
          reportFormValues: formValues,
          filters,
        });
        await handleUpdateStandardDeliveryDHCReport(report.id, updateReportInput, standardDeliveryReportFilters);
      } else {
        await handleCreateStandardDeliveryDHCReport(reportInput, standardDeliveryReportFilters);
      }

      const isMixedPath = pathname.includes('mixed');
      navigate(`../../../../../${isMixedPath ? '../direct/' : ''}${adEntityId}`);
      admanToast.success(`Report ${formValues.name} is successfully scheduled.`);
    } catch (e) {
      admanToast.error(`Something went wrong during scheduling report. Please try later.`);
    }
  };

  const resetFilters = (): void => {
    setFilter('cpm', undefined);
    setFilter('revenue', undefined);
    setFilter(LINE_ITEM_START_DATE_COLUMN_ID, undefined);
    setFilter(LINE_ITEM_END_DATE_COLUMN_ID, undefined);
    setFilter('adProduct', undefined);
    setFilter('publisher', undefined);
    setFilter('deliveryGoal', undefined);
    setFilter('lifetimeCompletions', undefined);
    resetAllFilters();
  };

  const resetFilter = (columnId: CommonDynamicFiltersFields): void => {
    if (columnId === 'startDate' || columnId === 'endDate') {
      setFilter(`lineItem${columnId.charAt(0).toUpperCase()}${columnId.slice(1)}`, undefined);
    } else {
      setFilter(columnId, undefined);
    }
    handleFilter({ id: columnId as DynamicFiltersFields });
  };

  const badges = getBadgeFilters(filters, dynamicFilters, resetFilter);

  const scheduleActionAllowed = report ? UPDATE_REPORT && doesUserOwnReport : CREATE_REPORT;
  const showReportMetrics = !error && !loading && rows.length;
  const showEmptyReportsState = !rows.length && !loading && !error;
  const showClearFilters =
    searchValue ||
    dynamicFilters?.cpm ||
    dynamicFilters?.deliveryGoal ||
    dynamicFilters?.revenue ||
    dynamicFilters?.startDate ||
    dynamicFilters?.adProduct ||
    dynamicFilters?.publisher ||
    dynamicFilters?.endDate ||
    dynamicFilters?.lifetimeCompletions;

  return (
    <>
      {BUILD_REPORT && <Charts staticFilters={storage?.getStorageData('staticFilters') || {}} />}
      <ReportContainer>
        <ReportTableHeader
          name="Report Campaigns"
          description="This service is not part of a regular syndicated rating service accredited by the MRC and the company has
              not requested accreditation. The company does provide one or more syndicated services that are accredited
              by the MRC."
          disabled={!!(loading || error || !scheduleActionAllowed)}
          badges={badges}
          onScheduleClicked={handleScheduleClicked}
          searchTerm={searchValue}
          onSearch={handleSearchDebounced}
          onGenerateReport={handleGenerateReport}
          loading={exportReportLoading}
        />
        <TableContainer>
          <StyledTable {...getTableProps()} data-testid="standardDeliveryDHCTable">
            <Header
              headerCellTestId="standardDeliveryDHCTableSort"
              headerGroups={headerGroups}
              handleSorting={handleSorting}
            />
            {showReportMetrics ? (
              <BodyWrapper
                rows={rows}
                prepareRow={prepareRow}
                getTableBodyProps={getTableBodyProps}
                tableCellDataId="standardDeliveryDHCTableCell"
              />
            ) : null}
          </StyledTable>
          {showEmptyReportsState && (
            <EmptyState
              title="No matching line items"
              subTitle={`Please adjust filters to show more line items.\n Have questions? Reach out to your Account Manager.`}
              filtersApplied={showClearFilters ? { onClearFilters: resetFilters } : undefined}
            />
          )}
          {loading && <Loader size={LoaderSize.big} />}
        </TableContainer>
      </ReportContainer>
      {error && <Notification type={NotificationType.error} description="Cannot get Report Data. Please try later." />}
      {showReportMetrics ? (
        <Pagination
          currentPage={paginationOptions.currentPage}
          entriesPerPage={paginationOptions.entriesPerPage}
          totalEntries={total || 0}
          onPageChanged={(pageNumber: number): void => handleChangePage(null, pageNumber)}
        />
      ) : null}
      {sidebarOpened && (
        <SideBar title={<>Schedule Report</>} onCancel={handleCloseSidebar} classesToSkip={[CAMPAIGNS_TOOLTIP_CLASS]}>
          <ReportForm
            initialValues={getInitialValuesForReportForm(report)}
            handleSubmitReport={onSaveReport}
            onCancel={handleCloseSidebar}
            badges={badges.map((badge) => ({ ...badge, onRemove: undefined }))}
            disabled={loading || savingReport || updatingReport}
            buttonText={report ? 'Save Changes' : 'Schedule Report'}
            newReport={!report}
            campaignIds={getCampaignIds(filters.campaigns)}
          />
        </SideBar>
      )}
    </>
  );
};

export default StandardDeliveryDHC;
